import {
  FunctionComponent,
  useState,
  SyntheticEvent,
  useCallback,
  useEffect,
} from 'react'
import styled, { css, useTheme } from 'styled-components'

import IconButton from '@components/Buttons/IconButton'
import { ArrowsVisibilityType, SwiperClass } from '@hooks/useSwiper'
import { desktopCSS } from '@measures/responsive'

export interface SwiperArrowsProps {
  swiperRef: React.RefObject<SwiperClass | null>
  arrowsVisibility?: ArrowsVisibilityType
  className?: string
}

const ArrowWrapper = styled.div<{
  direction: 'left' | 'right'
  isHidden: boolean
}>`
  ${({
    theme: {
      colors: { fillDefault },
    },
    direction,
    isHidden,
  }) => css`
    width: 24px;
    height: 100%;
    position: absolute;
    top: 0;
    display: flex;
    align-items: center;

    ${direction === 'left' &&
    css`
      left: 0;
      justify-content: flex-start;
      background: linear-gradient(
        90deg,
        ${fillDefault} 0%,
        rgba(255, 255, 255, 0) 100%
      );
    `}

    ${direction === 'right' &&
    css`
      right: 0;
      justify-content: flex-end;
      background: linear-gradient(
        270deg,
        ${fillDefault} 0%,
        rgba(255, 255, 255, 0) 100%
      );
    `}

    ${isHidden &&
    css`
      display: none;
    `}

    ${desktopCSS(css`
      width: 48px;
    `)}
  `}
`
const StyledWrapper = styled.div``

const StyledIconButtons = styled(IconButton)`
  display: none;

  ${desktopCSS(css`
    display: block;
  `)}
`

const SwiperArrows: FunctionComponent<SwiperArrowsProps> = ({
  className,
  arrowsVisibility = 'next',
  swiperRef,
}) => {
  const theme = useTheme()

  const [isLeftArrowHidden, setLeftArrowHidden] = useState<boolean>(() =>
    ['none', 'next'].includes(arrowsVisibility)
  )
  const [isRightArrowHidden, setRightArrowHidden] = useState<boolean>(() =>
    ['none', 'prev'].includes(arrowsVisibility)
  )

  useEffect(() => {
    setLeftArrowHidden(['none', 'next'].includes(arrowsVisibility))
    setRightArrowHidden(['none', 'prev'].includes(arrowsVisibility))
  }, [arrowsVisibility])

  const onLeftArrowClick = useCallback(
    (e?: SyntheticEvent) => {
      e?.preventDefault()
      swiperRef.current?.slideToDirection('prev')
    },
    [swiperRef]
  )

  const onRightArrownClick = useCallback(
    (e?: SyntheticEvent) => {
      e?.preventDefault()
      swiperRef.current?.slideToDirection('next')
    },
    [swiperRef]
  )

  return (
    <StyledWrapper className={className}>
      <ArrowWrapper direction="left" isHidden={isLeftArrowHidden}>
        <StyledIconButtons
          iconName="chevron-left"
          iconSize={24}
          iconColor={theme.colors.iconDefault}
          buttonSize={24}
          onClick={onLeftArrowClick}
          ariaLabel="left arrow"
        />
      </ArrowWrapper>
      <ArrowWrapper direction="right" isHidden={isRightArrowHidden}>
        <StyledIconButtons
          iconName="chevron-right"
          iconSize={24}
          iconColor={theme.colors.iconDefault}
          buttonSize={24}
          onClick={onRightArrownClick}
          ariaLabel="right arrow"
        />
      </ArrowWrapper>
    </StyledWrapper>
  )
}

export default SwiperArrows
